import React, { useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import { AppBar, Box, Container, Tab, Tabs } from "@material-ui/core";

import ManageQuizGroups from "./ManageQuizGroups";
import ManageQuizQuestions from "./ManageQuizQuestions";
import { useBmapi } from "../../utils/bmapi-context";

// -----------------------------
// Questions cache context
// -----------------------------
const QuestionsCacheContext = React.createContext(null);

export function useQuestionsCache() {
  return useContext(QuestionsCacheContext);
}

function QuestionsCacheProvider({ children }) {
  const {
    bmapi,
    businessId,
    notifyError,
    startLoading,
    stopLoading,
  } = useBmapi();

  const [questions, setQuestions] = useState([]); // cache delle domande
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // carica tutte le domande (o quelle necessarie) una sola volta
  const loadQuestionsOnce = useCallback(async () => {
    // se abbiamo già qualcosa in cache, non rifacciamo la fetch
    if (questions.length > 0 || loadingQuestions) return;

    setLoadingQuestions(true);
    startLoading();
    try {
      // API da usare per ottenere tutte le domande:
      // qui assumo bmapi.getQuizQuestionList con nessun filtro (category vuoto)
      // e lingua default. Se serve un'altra API, la sostituisci qui centralmente.
      const resp = await bmapi.getQuizQuestionList({
        business: businessId,
        lang: bmapi.getUserInfo().business.default_language,
        category: [], // tutte
      });
      setQuestions(resp || []);
    } catch (e) {
      notifyError("Errore caricamento domande: " + (e?.message || ""));
    } finally {
      stopLoading();
      setLoadingQuestions(false);
    }
  }, [
    bmapi,
    businessId,
    loadingQuestions,
    notifyError,
    questions.length,
    startLoading,
    stopLoading,
  ]);

  // funzione pensata per aggiornamenti ottimistici in futuro
  // per ora la teniamo, anche se non viene ancora usata
  const optimisticallyAddQuestion = useCallback(
    (newQ) => {
      setQuestions((curr) => {
        // evita duplicati (stessa id)
        if (curr.find((q) => q.id === newQ.id)) return curr;
        return [...curr, newQ];
      });
    },
    [setQuestions]
  );

  const value = {
    questions,
    setQuestions, // ci servirà in futuro per sync post-mutation vera
    loadingQuestions,
    loadQuestionsOnce,
    optimisticallyAddQuestion,
  };

  return (
    <QuestionsCacheContext.Provider value={value}>
      {children}
    </QuestionsCacheContext.Provider>
  );
}

QuestionsCacheProvider.propTypes = {
  children: PropTypes.node,
};

// -----------------------------
// Tabs / wrapper
// -----------------------------

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}
TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function ManageQuiz() {
  const [value, setValue] = useState(0);
  const handleChange = (_event, newValue) => setValue(newValue);

  return (
    <QuestionsCacheProvider>
      <Container maxWidth="md">
        <AppBar position="static" color="default">
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="quiz tabs"
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Quiz" {...a11yProps(0)} />
            <Tab label="Domande" {...a11yProps(1)} />
          </Tabs>
        </AppBar>

        <TabPanel value={value} index={0}>
          <ManageQuizGroups />
        </TabPanel>

        <TabPanel value={value} index={1}>
          <ManageQuizQuestions />
        </TabPanel>
      </Container>
    </QuestionsCacheProvider>
  );
}
